import useGetEventFromApp from "hooks/useGetEventFromApp";
import { useEffect } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import { userAtom } from "recoil/userAtom";
import { useColorScheme as useMaterialColorTheme } from "@mui/material/styles";
import { Stack, Typography, useColorScheme } from "@mui/joy";
import { useLocation } from "react-router-dom";
import { sendMessage } from "utils/webview";
import { deviceAtom } from "recoil/deviceAtom";
import useOpenTask from "hooks/useOpenTask";
import { localizationAtom } from "recoil/localizationAtom";
import i18n from "translation/i18n";
import dayjs from "dayjs";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN } from "api/userApi";
import { useQueryClient } from "@tanstack/react-query";
import useNavigatorOnLine from "hooks/useNavigatorOnline";
import { setAppIdHeaders, setAuthHeaders } from "api";

export default function Settings({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useRecoilState(localizationAtom);
  const queryClient = useQueryClient();

  const location = useLocation();

  const openPayment = useOpenTask({ taskKey: `payment` });
  const setPermanantDataState = useSetRecoilState(permanentDataAtom);
  const [user, setUser] = useRecoilState(userAtom);
  const setDeviceState = useSetRecoilState(deviceAtom);
  const parsedMessageFromApp = useGetEventFromApp();

  const { mode, setMode } = useColorScheme();
  const { mode: materialMode, setMode: setMaterialMode } =
    useMaterialColorTheme();

  const isNetworkConnected = useNavigatorOnLine();

  useEffect(() => {
    const localeWithDefault =
      locale.startsWith("ko") || locale.startsWith("en") ? locale : "en";
    i18n.changeLanguage(localeWithDefault);
    dayjs.locale(localeWithDefault);
  }, [locale]);

  useEffect(() => {
    sendMessage(
      JSON.stringify({
        event: "location",
        params: {
          pathname: location.pathname,
        },
      })
    );
  }, [location]);

  useEffect(() => {
    sendMessage(
      JSON.stringify({
        event: "ready",
      })
    );
    setMode("light");
    setMaterialMode("light");
  }, []);

  const handleMessageFromApp = (parsedMessageFromApp: {
    event: string;
    params: any;
  }) => {
    if (parsedMessageFromApp?.event === "isWebview") {
      setPermanantDataState((state) => ({ ...state, isWebview: true }));
    }
    if (parsedMessageFromApp?.event === "appVersion") {
      const appVersion = parsedMessageFromApp.params.version;
      setPermanantDataState((state) => ({ ...state, appVersion: appVersion }));
    }
    if (parsedMessageFromApp?.event === "mode") {
      const colorScheme = parsedMessageFromApp.params.colorScheme;
      if (mode !== colorScheme) {
        setMode(colorScheme);
      }
      if (materialMode !== colorScheme) {
        setMaterialMode(colorScheme);
      }
    }
    if (parsedMessageFromApp?.event === "accessToken") {
      const accessToken = parsedMessageFromApp.params.value;
      setAuthHeaders(accessToken);
      setUser((user) => ({
        ...user,
        accessToken: accessToken,
      }));
    }
    if (parsedMessageFromApp?.event === "appId") {
      const appId = parsedMessageFromApp.params.value;
      if (appId) {
        setAppIdHeaders(appId);
      }
      setUser((data) => ({
        ...data,
        appId: appId,
      }));
    }
    if (parsedMessageFromApp?.event === "appLock") {
      const isAppLockEnabled = parsedMessageFromApp.params.isEnabled;
      setPermanantDataState((state) => ({
        ...state,
        isAppLockEnabled: isAppLockEnabled,
      }));
    }
    if (parsedMessageFromApp?.event === "pushNotification_result") {
      const status = parsedMessageFromApp.params.status;
      if (status) {
        setDeviceState((prevState) => {
          return {
            ...prevState,
            pushNotificationStatus: status,
          };
        });
      }
    }

    if (parsedMessageFromApp?.event === "open") {
      const tab = parsedMessageFromApp.params.tab;
      if (tab === "payment") {
        openPayment();
      }
    }
    if (parsedMessageFromApp?.event === "refetch_user") {
      queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);
    }
    if (parsedMessageFromApp?.event === "openUrl") {
      const url = parsedMessageFromApp.params.url;
      window.location.href = url;
    }
  };

  useEffect(() => {
    if (Array.isArray(parsedMessageFromApp)) {
      parsedMessageFromApp.forEach((each) => handleMessageFromApp(each));
    } else {
      if (parsedMessageFromApp) {
        handleMessageFromApp(parsedMessageFromApp);
      }
    }
  }, [parsedMessageFromApp]);

  useEffect(() => {
    // 네이티브 브리지 존재 여부로 즉시 감지
    const isWV =
      !!window.ReactNativeWebView ||
      !!window.webkit?.messageHandlers ||
      !!window.AndroidInterface;

    // 초기값을 확정(true/false)해 undefined 상태를 없앰
    setPermanantDataState((state) => ({ ...state, isWebview: isWV }));
  }, [setPermanantDataState]);

  return (
    <>
      {!isNetworkConnected ? (
        <Stack
          direction={"column"}
          sx={{
            backgroundColor: "background.level2",
            width: "100%",
            height: "100%",
          }}
          justifyContent="center"
          alignItems={"center"}
          spacing={1}
        >
          <Typography variant="H3" sx={{ fontWeight: 700 }}>
            인터넷 연결이 원활하지 않아요
          </Typography>
          <Typography variant="Body">
            연결 상태를 확인하고 앱 또는 브라우저를 완전히 종료한 후 다시
            실행해주세요.
          </Typography>
        </Stack>
      ) : (
        children
      )}
    </>
  );
}
